import React, { useState, useEffect, useRef } from 'react';
import { db, auth, provider } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';

// CHANGE THIS TO SITSO'S ACTUAL GMAIL
const ADMIN_EMAIL = "sdornyo10@gmail.com"; 

const AdminSitso = () => {
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create a reference to specifically target the form for scrolling
  const formRef = useRef(null);
  
  // Form States - Now tied to localStorage so they survive a page refresh
  const [name, setName] = useState(localStorage.getItem('vf_admin_name') || '');
  const [category, setCategory] = useState(localStorage.getItem('vf_admin_category') || 'ARTISTES');
  const [flag, setFlag] = useState(localStorage.getItem('vf_admin_flag') || '');
  const [editId, setEditId] = useState(localStorage.getItem('vf_admin_editId') || null);

  // Auto-save form progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vf_admin_name', name);
    localStorage.setItem('vf_admin_category', category);
    localStorage.setItem('vf_admin_flag', flag);
    if (editId) {
      localStorage.setItem('vf_admin_editId', editId);
    } else {
      localStorage.removeItem('vf_admin_editId');
    }
  }, [name, category, flag, editId]);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser && currentUser.email === ADMIN_EMAIL) {
        fetchAllData();
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchAllData = async () => {
    try {
      // Fetch Clients
      const clientsSnapshot = await getDocs(collection(db, "clients"));
      const clientsData = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientsData);

      // Fetch Reviews
      const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
      const reviewsSnapshot = await getDocs(q);
      const reviewsData = reviewsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReviews(reviewsData);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      if (result.user.email !== ADMIN_EMAIL) {
        alert("Access Denied. You are not authorized.");
        await signOut(auth);
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  // --- CLIENTS LOGIC ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (editId) {
        // Update existing
        await updateDoc(doc(db, "clients", editId), { name, category, flag });
        alert("Updated successfully!");
      } else {
        // Add new
        await addDoc(collection(db, "clients"), { 
          name, 
          category, 
          flag,
          createdAt: serverTimestamp() 
        });
        alert("Added successfully!");
      }
      
      // Clear specific form inputs but LEAVE category so they can quickly add multiple items to the same group
      setName('');
      setFlag('');
      setEditId(null);
      fetchAllData();
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save.");
    }
  };

  const handleEdit = (client) => {
    setEditId(client.id);
    setName(client.name);
    setCategory(client.category);
    setFlag(client.flag || '');
    
    // Explicitly scroll the form element into the center of the view
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      try {
        await deleteDoc(doc(db, "clients", id));
        fetchAllData();
      } catch (error) {
        console.error("Error deleting:", error);
      }
    }
  };

  // --- REVIEWS LOGIC ---
  const handleToggleReviewVisibility = async (id, currentVisibility) => {
    try {
      // If isVisible is undefined (old reviews), assume it was true, so we make it false.
      const newVisibility = currentVisibility === undefined ? false : !currentVisibility;
      await updateDoc(doc(db, "reviews", id), { isVisible: newVisibility });
      fetchAllData();
    } catch (error) {
      console.error("Error toggling review visibility:", error);
      alert("Failed to update review.");
    }
  };

  const handleDeleteReview = async (id) => {
    if (window.confirm("Are you sure you want to PERMANENTLY delete this review?")) {
      try {
        await deleteDoc(doc(db, "reviews", id));
        fetchAllData();
      } catch (error) {
        console.error("Error deleting review:", error);
      }
    }
  };

  // If loading or checking auth
  if (loading) return <section><div className="main-content"><p>Loading Admin...</p></div></section>;

  // If not logged in or wrong email
  if (!user || user.email !== ADMIN_EMAIL) {
    return (
      <section style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="card center-card" style={{ maxWidth: '400px', width: '100%' }}>
          <h2>Admin Access</h2>
          <p style={{ color: 'var(--muted)', margin: '15px 0' }}>Strictly restricted area.</p>
          <button onClick={handleLogin} className="btn">Sign In</button>
        </div>
      </section>
    );
  }

  return (
    <section className="main-content" style={{ marginTop: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Welcome, Sitso</h1><br />
        <button onClick={handleLogout} className="btn secondary small-btn">Logout</button>
      </div>

    <h2>Manage lists of artistes and others including reviews</h2>

      {/* Form to Add/Edit - Attached the formRef here */}
      <div className="card dark-red-card" style={{ marginBottom: '30px' }} ref={formRef}>
        <h3 className="card-title">{editId ? "Edit Entry" : "Add New Entry"}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <select 
            value={category} 
            onChange={(e) => setCategory(e.target.value)} 
            className="form-input"
          >
            <option value="ARTISTES" style={{ color: '#000' }}>Artistes</option>
            <option value="CHOIRS" style={{ color: '#000' }}>Choirs</option>
            <option value="PRODUCERS" style={{ color: '#000' }}>Producers</option>
            <option value="ORGANIZATIONS" style={{ color: '#000' }}>Organizations</option>
          </select>
          
          <input 
            type="text" 
            placeholder="Name (e.g. Min. Celestine Donkor)" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            className="form-input" 
            required 
          />

          {category === 'ARTISTES' && (
            <input 
              type="text" 
              placeholder="Flag Emoji (e.g. 🇬🇭) - Optional" 
              value={flag} 
              onChange={(e) => setFlag(e.target.value)} 
              className="form-input" 
            />
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn" style={{ background: 'white', color: 'var(--primary-red)' }}>
              {editId ? "Update" : "Save"}
            </button>
            {editId && (
              <button type="button" onClick={() => { setEditId(null); setName(''); setFlag(''); }} className="btn secondary">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List of current data (Clients) */}
      <div className="card-grid">
        {['ARTISTES', 'CHOIRS', 'PRODUCERS', 'ORGANIZATIONS'].map((cat) => (
          <div key={cat} className="card org-card">
            <h3 style={{ marginBottom: '15px', color: 'var(--primary-red)' }}>{cat}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {clients.filter(c => c.category === cat).map((client) => (
                <div key={client.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: '8px' }}>
                  <span>{client.name} {client.flag}</span>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button type="button" onClick={() => handleEdit(client)} className="btn small-btn" style={{ padding: '4px 8px', fontSize: '0.8rem' }}>Edit</button>
                    <button type="button" onClick={() => handleDelete(client.id)} className="btn small-btn" style={{ padding: '4px 8px', fontSize: '0.8rem', background: '#333' }}>X</button>
                  </div>
                </div>
              ))}
              {clients.filter(c => c.category === cat).length === 0 && <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>No entries yet.</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Manage Reviews Section */}
      <div className="card org-card" style={{ marginTop: '30px' }}>
        <h3 style={{ marginBottom: '15px', color: 'var(--primary-red)' }}>MANAGE REVIEWS</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {reviews.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>No reviews yet.</p>
          ) : (
            reviews.map((rev) => (
              <div key={rev.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ margin: 0 }}><strong>{rev.name}</strong> <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{rev.isVisible === false ? '(Hidden)' : '(Visible)'}</span></p>
                <p style={{ margin: 0, fontStyle: 'italic', fontSize: '0.95rem' }}>"{rev.message}"</p>
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                  <button 
                    onClick={() => handleToggleReviewVisibility(rev.id, rev.isVisible)} 
                    className="btn small-btn" 
                    style={{ padding: '4px 12px', fontSize: '0.8rem', background: rev.isVisible !== false ? '#ff9800' : '#4caf50', color: 'white' }}
                  >
                    {rev.isVisible !== false ? 'Hide from Website' : 'Unhide'}
                  </button>
                  <button 
                    onClick={() => handleDeleteReview(rev.id)} 
                    className="btn small-btn" 
                    style={{ padding: '4px 12px', fontSize: '0.8rem', background: '#333', color: 'white' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </section>
  );
};

export default AdminSitso;